// Generic in-visual drill-down (Phase 7).
//
// When a visual's category group carries MORE THAN ONE dimension (e.g.
// Rows = [country, state]), a click on a category re-renders the SAME visual one
// level deeper — filtered to the clicked value and re-grouped on the next
// dimension — instead of firing drill-through. Aggregation is done client-side
// from the finest-grain rows the data engine already returns (every
// non-aggregated column lands in GROUP BY server-side), so SUM/MIN/MAX are
// exact; COUNT is the sum of partial counts; AVG is the mean of partials
// (documented approximation at upper levels).

import { mapVisualData, pickGroup } from './dataUtils'
import type { MappedColumn, MappedData } from './dataUtils'
import type { VisualJsonData, VisualLayout } from './types'

// Category-type groups, in the order a chart family would treat as its
// "dimension" axis (classic Rows, plus the new chart families' names).
const CATEGORY_GROUPS = ['rows', 'label', 'stage', 'category', 'axis']

const SEP = String.fromCharCode(1)

export interface DrillLevel {
  /** Column NAME of the dimension drilled at this level. */
  dim: string
  /** RAW clicked value (used for equality filtering — never the formatted label). */
  value: unknown
  /** Display label of the clicked value (for the breadcrumb). */
  label: string
}

export interface DrillState {
  /** The ordered dimension columns of the category group. */
  dims: MappedColumn[]
  /** Lowercased StandardName of the category group being drilled. */
  groupKey: string
  /** One entry per level already drilled into (top level = empty). */
  levels: DrillLevel[]
}

// The first category-type group with ≥2 VISIBLE non-calc dimension columns.
// Returns null when the visual has no multi-dimension category group.
export function detectDimHierarchy(md: MappedData): { groupKey: string; dims: MappedColumn[] } | null {
  for (const key of CATEGORY_GROUPS) {
    const cols = (md.groups[key] || []).filter((c) => !c.hidden && !c.isCalc)
    if (cols.length >= 2) return { groupKey: key, dims: cols }
  }
  return null
}

// Raw-value equality with a string-compare fallback (mirrors rowFromPoint's
// dual match) — drill filtering must always use RAW values.
function rawEq(a: unknown, b: unknown): boolean {
  if (a === b) return true
  return String(a ?? '') === String(b ?? '')
}

// Aggregate partial values from the finest-grain rows for one measure.
//   SUM / COUNT → sum of partials (COUNT is exact: partials are counts)
//   MIN / MAX   → exact
//   AVG         → mean of partials (approximation — documented)
// Exported because the client-side quick filter re-aggregates the same way when
// it filters on a column finer than what a visual displays (see quickFilter.ts).
export function aggregate(values: unknown[], agg?: string): number | null {
  const nums: number[] = []
  for (const v of values) {
    const n = Number(v)
    if (!Number.isNaN(n) && v !== '' && v != null) nums.push(n)
  }
  if (nums.length === 0) return null
  const fn = String(agg || '').toUpperCase()
  if (fn.includes('MIN')) return Math.min(...nums)
  if (fn.includes('MAX')) return Math.max(...nums)
  if (fn.includes('AVG') || fn.includes('AVERAGE')) {
    return nums.reduce((a, b) => a + b, 0) / nums.length
  }
  // SUM, COUNT (sum of partial counts), and default.
  return nums.reduce((a, b) => a + b, 0)
}

// Clone the layout with the drilled category group rewritten to ONLY the
// current dimension's column (so the chart renders a single dimension), keeping
// every other group (values, series/columns) intact.
function cloneLayoutWithDim(layout: VisualLayout, groupKey: string, dim: MappedColumn): VisualLayout {
  const clone = JSON.parse(JSON.stringify(layout)) as VisualLayout
  for (const g of (clone.ColumnGroups ?? []) as Array<{ StandardName?: unknown; COLUMNS?: Array<Record<string, unknown>> }>) {
    if (String(g.StandardName ?? '').toLowerCase() !== groupKey) continue
    const cols = g.COLUMNS ?? []
    const keep = cols.find((c) => String(c.NAME ?? c.name) === dim.name)
    g.COLUMNS = keep ? [keep] : (cols.length ? [cols[0]] : [])
  }
  return clone
}

// Derive the visual's jsonData + layout for the CURRENT drill level: filter to
// the drilled values, re-group on the next dimension (+ any series columns so
// grouped/stacked charts keep their series), and re-aggregate the measures.
export function deriveDrillView(
  jsonData: VisualJsonData,
  visualLayout: VisualLayout,
  state: DrillState,
): { jsonData: VisualJsonData; visualLayout: VisualLayout } {
  const md = mapVisualData(jsonData, visualLayout)
  const level = state.levels.length
  const currentDim = state.dims[level]
  if (!currentDim) return { jsonData, visualLayout }

  // 1. Filter rows to the already-drilled level values.
  const filtered = md.rows.filter((r) =>
    state.levels.every((lv) => rawEq(r[lv.dim], lv.value)),
  )

  // 2. Re-group by [currentDim, ...seriesColumns]. Series/Columns groups MUST
  //    stay in the grouping or grouped/stacked charts collapse.
  const seriesCols = [...pickGroup(md, 'columns'), ...pickGroup(md, 'series')]
  const valueCols = pickGroup(md, 'values', 'value')
  const groupCols = [currentDim, ...seriesCols]

  const groups = new Map<string, { key: Record<string, unknown>; rows: Array<Record<string, unknown>> }>()
  for (const r of filtered) {
    const gk = groupCols.map((c) => String(r[c.name] ?? '')).join(SEP)
    let g = groups.get(gk)
    if (!g) {
      const keyObj: Record<string, unknown> = {}
      for (const c of groupCols) keyObj[c.name] = r[c.name]
      g = { key: keyObj, rows: [] }
      groups.set(gk, g)
    }
    g.rows.push(r)
  }

  // 3. Aggregate each measure per group.
  const outRows: Array<Record<string, unknown>> = []
  for (const g of groups.values()) {
    const row: Record<string, unknown> = { ...g.key }
    for (const vc of valueCols) {
      row[vc.name] = aggregate(g.rows.map((r) => r[vc.name]), vc.agg)
    }
    outRows.push(row)
  }

  // 4. Clone the layout with the category group down to the single current dim.
  const newLayout = cloneLayoutWithDim(visualLayout, state.groupKey, currentDim)
  const newJsonData = [md.schema, ...outRows] as unknown as VisualJsonData
  return { jsonData: newJsonData, visualLayout: newLayout }
}

// Breadcrumb pieces for the header strip: one crumb per drilled level plus the
// dimension the current level is showing (null at the deepest level).
export function drillBreadcrumb(state: DrillState): { crumbs: string[]; currentDimLabel: string | null } {
  const dimLabel = (name: string) => state.dims.find((d) => d.name === name)?.label || name
  const crumbs = state.levels.map((lv) => `${dimLabel(lv.dim)}: ${lv.label}`)
  const current = state.dims[state.levels.length]
  return { crumbs, currentDimLabel: current ? current.label : null }
}

// True when this drill state still has a deeper level to go.
export function canDrillDeeper(state: DrillState): boolean {
  return state.levels.length < state.dims.length - 1
}

// Merge the drilled parent values into an emitted leaf row (additive keys) so a
// drill-through PARAM_MAPPING and underlying-data filters see the FULL dimension
// context ({country, state, …}), not just the leaf dimension.
export function mergeDrillValues(
  row: Record<string, unknown>,
  state: DrillState | null,
): Record<string, unknown> {
  if (!state || state.levels.length === 0) return row
  const merged: Record<string, unknown> = { ...row }
  for (const lv of state.levels) {
    if (merged[lv.dim] === undefined) merged[lv.dim] = lv.value
  }
  return merged
}

// The row-level rows behind a clicked point, computed ENTIRELY client-side from
// the dataset already loaded into the visual — no backend round-trip. Returns
// `[schemaRow, ...matchingRows]`: every rendered row whose values satisfy the
// equality `filters` (keyed by column NAME, RAW values). Empty filters → all
// rows (the whole result set, used by the ⋮ "View underlying data" item). The
// grain is the visual's own grain — the finest the data engine returned for it.
export function underlyingClientRows(
  jsonData: VisualJsonData,
  visualLayout: VisualLayout,
  filters: Record<string, unknown>,
): VisualJsonData {
  const md = mapVisualData(jsonData, visualLayout)
  const keys = Object.keys(filters || {})
  const matched = keys.length === 0
    ? md.rows
    : md.rows.filter((r) => keys.every((k) => rawEq(r[k], filters[k])))
  return [md.schema, ...matched] as unknown as VisualJsonData
}

// The dimension filters behind a clicked point: every non-Values, non-calc,
// visible column that's present in the row (drives the Underlying Data view).
export function dimensionFiltersFromRow(
  row: Record<string, unknown>,
  visualLayout: VisualLayout,
): Record<string, unknown> {
  const md = mapVisualData([{}, row] as unknown as VisualJsonData, visualLayout)
  const valueNames = new Set(pickGroup(md, 'values', 'value').map((c) => c.name))
  const out: Record<string, unknown> = {}
  for (const group of Object.values(md.groups)) {
    for (const c of group) {
      if (c.hidden || c.isCalc || valueNames.has(c.name)) continue
      if (c.name in row && row[c.name] !== undefined) out[c.name] = row[c.name]
    }
  }
  return out
}
